<template>
  <div class="min-h-screen bg-gray-50">
    <HeaderDefault />

    <!-- Hero Section -->
    <section class="bg-gradient-to-b from-primary to-primary/70 py-16">
      <div class="max-w-screen-xl mx-auto px-4">
        <div class="text-center max-w-3xl mx-auto">
          <h1 class="text-4xl md:text-5xl font-bold text-white mb-4">
            Members Directory
            <span class="capitalize">- {{ route.params.id }}</span>
          </h1>
          <p class="text-primary-light text-lg">
            Discover academic professionals whose names begin with '{{
              route.params.id.toUpperCase()
            }}'
          </p>
        </div>
      </div>
    </section>

    <!-- Directory Section -->
    <section class="py-12">
      <div class="max-w-screen-xl mx-auto px-4">
        <!-- Alphabet Navigation -->
        <div class="flex flex-wrap justify-center gap-2 mb-12">
          <nuxt-link
            v-for="letter in lowercaseAlphabets"
            :key="letter"
            :to="'/people/' + letter"
            class="w-10 h-10 flex items-center justify-center rounded-lg font-medium transition-all uppercase"
            :class="
              route.params.id === letter
                ? 'bg-primary text-white shadow-lg scale-110'
                : 'bg-primary/10 text-primary hover:bg-primary/20'
            "
          >
            {{ letter }}
          </nuxt-link>
        </div>

        <!-- Members Grid -->
        <div
          v-if="!pending && speakers.data.length > 0"
          class="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
        >
          <nuxt-link
            v-for="speaker in speakers.data"
            :key="speaker.id"
            :to="`/profile/${speaker.id}`"
            class="block transition-transform hover:scale-[1.02] duration-300"
          >
            <ProfileCard
              :profile="speaker.attributes"
            />
          </nuxt-link>
        </div>

        <!-- Empty State -->
        <div
          v-else-if="!pending && speakers.data.length === 0"
          class="bg-white rounded-xl shadow-sm p-8 text-center"
        >
          <Icon
            name="heroicons:user-group"
            class="w-16 h-16 text-primary/30 mx-auto mb-4"
          />
          <h2 class="text-2xl font-semibold text-gray-900 mb-2">
            No Members Found
          </h2>
          <p class="text-gray-600 mb-6 max-w-md mx-auto">
            We couldn't find any members whose names begin with '{{
              route.params.id.toUpperCase()
            }}'. Consider joining our community to be the first!
          </p>
          <nuxt-link
            to="/become-a-member"
            class="inline-flex items-center px-6 py-3 text-white bg-primary rounded-lg hover:bg-primary/90 transition-all group"
          >
            Become a Member

            <Icon
              name="heroicons:arrow-rightp"
              class="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform"
            />
          </nuxt-link>
        </div>

        <!-- Loading State -->
        <div v-else class="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <div
            v-for="n in 6"
            :key="n"
            class="bg-white rounded-xl shadow-sm p-6 animate-pulse"
          >
            <div class="flex justify-between mb-4">
              <div class="space-y-3 flex-1">
                <div class="h-6 bg-gray-200 rounded w-3/4"></div>
                <div class="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
              <div class="w-6 h-6 bg-gray-200 rounded-full"></div>
            </div>
            <div class="flex gap-2 mt-4">
              <div class="h-6 w-20 bg-gray-200 rounded-full"></div>
              <div class="h-6 w-20 bg-gray-200 rounded-full"></div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <FooterDefault />
  </div>
</template>

<script setup lang="ts">
const route = useRoute();

const lowercaseAlphabets = Array.from({ length: 26 }, (_, i) =>
  String.fromCharCode(97 + i)
);

const { data: speakers, pending } = await useAsyncData(
  "filtered-speakers",
  () =>
    $fetch(
      `https://backend.postfeed.in/api/aa-users?filters[first_name][$startsWithi]=${route.params.id}&pagination[pageSize]=450&sort=first_name:asc`
    )
);

useHead({
  title: `Members Directory - ${route.params.id.toUpperCase()} | Academic Alerts`,
  meta: [
    {
      name: "description",
      content: `Browse academic professionals and researchers whose names begin with ${route.params.id.toUpperCase()}.`,
    },
  ],
});
</script>
